import React from 'react'
import {
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Input,
  List,
  Row,
  Tag,
  Typography
} from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { productApi } from 'api/product'
import { sellerApi } from 'api/seller'
import { ICategoryWithSelectedSellers } from 'models/product'
import { ISeller } from 'models/seller'
import {
  HIDE_INACTIVE_SELLERS_LABEL,
  isActiveSellerWithProducts
} from 'shared/utils/sellerFilters'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'

const MAX_SELECTED_SELLERS = 5
const LIST_HEIGHT = 420

const getSellerName = (seller: ISeller) =>
  seller.shop_details?.shop_name?.trim() ||
  seller.user_id?.full_name ||
  'Без названия'

export const SelectedSellers: React.FC = () => {
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [categories, setCategories] = React.useState<
    ICategoryWithSelectedSellers[]
  >([])
  const [sellers, setSellers] = React.useState<ISeller[]>([])
  const [activeCategoryId, setActiveCategoryId] = React.useState('')
  // выбор по категориям хранится черновиком, пока админ не нажмёт «Сохранить»
  const [drafts, setDrafts] = React.useState<Record<string, string[]>>({})
  const [search, setSearch] = React.useState('')
  const [hideInactive, setHideInactive] = React.useState(true)

  const getData = async () => {
    try {
      setIsLoading(true)
      const [categoriesResponse, sellersResponse] = await Promise.all([
        productApi.getCategories({ parsed: 'false' }),
        sellerApi.getSearchSellers(true)
      ])

      const loadedCategories = (
        categoriesResponse.data as ICategoryWithSelectedSellers[]
      )
        .slice()
        .sort((a, b) => (a.number ?? 0) - (b.number ?? 0))

      setCategories(loadedCategories)
      setDrafts(
        Object.fromEntries(
          loadedCategories.map((category) => [
            category._id,
            category.selected_sellers ?? []
          ])
        )
      )
      setSellers(sellersResponse.data)
      setActiveCategoryId((prev) => prev || loadedCategories[0]?._id || '')
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    getData()
  }, [])

  const sellersById = React.useMemo(
    () => new Map(sellers.map((seller) => [seller._id, seller])),
    [sellers]
  )

  const selectedIds = drafts[activeCategoryId] ?? []
  const savedIds =
    categories.find((category) => category._id === activeCategoryId)
      ?.selected_sellers ?? []
  const isDirty =
    selectedIds.slice().sort().join(',') !== savedIds.slice().sort().join(',')

  const availableSellers = React.useMemo(() => {
    const query = search.trim().toLowerCase()
    return sellers
      .filter((seller) => !hideInactive || isActiveSellerWithProducts(seller))
      .filter((seller) => !selectedIds.includes(seller._id))
      .filter((seller) => getSellerName(seller).toLowerCase().includes(query))
      .sort((a, b) => getSellerName(a).localeCompare(getSellerName(b), 'ru'))
  }, [sellers, selectedIds, search, hideInactive])

  const addSeller = (sellerId: string) => {
    if (selectedIds.length >= MAX_SELECTED_SELLERS) {
      return showMessage(
        'info',
        `В одной категории максимум ${MAX_SELECTED_SELLERS} магазинов`,
        ''
      )
    }
    setDrafts((prev) => ({
      ...prev,
      [activeCategoryId]: [...selectedIds, sellerId]
    }))
  }

  const removeSeller = (sellerId: string) => {
    setDrafts((prev) => ({
      ...prev,
      [activeCategoryId]: selectedIds.filter((id) => id !== sellerId)
    }))
  }

  const saveCategory = async () => {
    if (!activeCategoryId) return
    try {
      setIsSaving(true)
      await productApi.updateCategorySelectedSellers(activeCategoryId, {
        seller_ids: selectedIds
      })
      setCategories((prev) =>
        prev.map((category) =>
          category._id === activeCategoryId
            ? { ...category, selected_sellers: selectedIds }
            : category
        )
      )
      showMessage('success', 'Список продвигаемых магазинов сохранён', '')
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSaving(false)
    }
  }

  const renderSellerName = (seller: ISeller) => (
    <span>
      {getSellerName(seller)}{' '}
      {seller.is_disabled && <Tag color='default'>отключён</Tag>}
    </span>
  )

  return (
    <div style={{ paddingBottom: 80 }}>
      <Card style={{ marginBottom: 16 }} title='Как это работает'>
        <Typography.Paragraph>
          Избранные продавцы — это селлеры, товары которых покупатели видят
          первыми. Для каждой категории можно выбрать до{' '}
          {MAX_SELECTED_SELLERS} магазинов.
        </Typography.Paragraph>
        <Typography.Paragraph>
          <Typography.Text strong>Где показываются товары:</Typography.Text>
          <ul style={{ marginTop: 8, marginBottom: 0 }}>
            <li>
              <Typography.Text strong>Главная страница</Typography.Text> — сайт и
              приложение. Первые 10 карточек (примерно два первых ряда) отдаются
              продвигаемым магазинам. Здесь участвуют магазины из всех категорий
              сразу: чем больше категорий вы заполните, тем меньше карточек
              достанется каждому магазину. Если на сайте включены пулы во
              вкладке «Выдача», продвигаемые магазины идут сразу после них.
            </li>
            <li>
              <Typography.Text strong>
                Каталог категории и её подкатегорий
              </Typography.Text>{' '}
              — сайт и приложение. Первые 20 карточек списка отдаются
              продвигаемым магазинам этой категории. Работает, если в категории
              набирается хотя бы 50 товаров, иначе список показывается как
              обычно.
            </li>
          </ul>
        </Typography.Paragraph>
        <Typography.Paragraph>
          <Typography.Text strong>Как делятся места:</Typography.Text> поровну
          между выбранными магазинами. Например, 5 магазинов в категории — это
          примерно по 4 карточки на каждого из 20 мест в каталоге. Если у
          магазина не хватает подходящих товаров, его место занимает другой
          продвигаемый магазин, а если товаров нет ни у кого — обычная выдача.
        </Typography.Paragraph>
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          <Typography.Text strong>Что ещё важно знать:</Typography.Text>
          <ul style={{ marginTop: 8, marginBottom: 0 }}>
            <li>
              Порядок магазинов в правом столбце ни на что не влияет: товары
              внутри блока перемешиваются и при каждом обновлении страницы
              показываются разные.
            </li>
            <li>
              На главной покупатель не увидит одни и те же товары повторно
              примерно 5 минут — листая ленту дальше, он увидит другие товары
              тех же магазинов.
            </li>
            <li>
              Галочка «Скрыть неактивных» оставляет в списке только
              подтверждённые и включённые магазины хотя бы с одним
              опубликованным товаром — остальным продвижение ничего не даст.
              Снимите её, чтобы увидеть всех.
            </li>
            <li>
              Участвуют только опубликованные товары с фотографиями. Товары
              отключённых магазинов не показываются.
            </li>
            <li>
              Изменения вступают в силу сразу и на сайте, и в приложении —
              обновлять приложение не нужно.
            </li>
            <li>
              Вкладка «Выдача» рядом настраивает первые ряды только на главной
              странице сайта, а этот список работает и в приложении.
            </li>
          </ul>
        </Typography.Paragraph>
      </Card>

      <Row gutter={16}>
        <Col xs={24} lg={6}>
          <Card title='Категория' loading={isLoading} bodyStyle={{ padding: 0 }}>
            <div style={{ maxHeight: LIST_HEIGHT, overflowY: 'auto' }}>
              <List
                dataSource={categories}
                renderItem={(category) => {
                  const count = (drafts[category._id] ?? []).length
                  const isActive = category._id === activeCategoryId
                  return (
                    <List.Item
                      onClick={() => setActiveCategoryId(category._id)}
                      style={{
                        cursor: 'pointer',
                        padding: '8px 16px',
                        background: isActive ? '#e6f4ff' : undefined
                      }}
                    >
                      <span>{category.name}</span>
                      <Tag color={count > 0 ? 'blue' : 'default'}>
                        {count}/{MAX_SELECTED_SELLERS}
                      </Tag>
                    </List.Item>
                  )
                }}
              />
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={9}>
          <Card
            title='Все продавцы'
            loading={isLoading}
            bodyStyle={{ padding: 0 }}
            extra={
              <Checkbox
                checked={hideInactive}
                onChange={(e) => setHideInactive(e.target.checked)}
              >
                <Typography.Text type='secondary' style={{ fontSize: 12 }}>
                  {HIDE_INACTIVE_SELLERS_LABEL}
                </Typography.Text>
              </Checkbox>
            }
          >
            <div style={{ padding: 12 }}>
              <Input
                allowClear
                placeholder='Поиск по названию магазина'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div style={{ maxHeight: LIST_HEIGHT, overflowY: 'auto' }}>
              <List
                dataSource={availableSellers}
                locale={{ emptyText: <Empty description='Магазины не найдены' /> }}
                renderItem={(seller) => (
                  <List.Item
                    style={{ padding: '8px 16px' }}
                    actions={[
                      <Button
                        key='add'
                        size='small'
                        icon={<RightOutlined />}
                        disabled={selectedIds.length >= MAX_SELECTED_SELLERS}
                        onClick={() => addSeller(seller._id)}
                      />
                    ]}
                  >
                    {renderSellerName(seller)}
                  </List.Item>
                )}
              />
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={9}>
          <Card
            title={
              <span>
                Избранные ({selectedIds.length}/
                {MAX_SELECTED_SELLERS}){' '}
                {isDirty && <Tag color='warning'>не сохранено</Tag>}
              </span>
            }
            loading={isLoading}
            bodyStyle={{ padding: 0 }}
            extra={
              <Button
                type='primary'
                size='small'
                loading={isSaving}
                disabled={!isDirty}
                onClick={saveCategory}
              >
                Сохранить
              </Button>
            }
          >
            <div style={{ maxHeight: LIST_HEIGHT, overflowY: 'auto' }}>
              <List
                dataSource={selectedIds}
                locale={{
                  emptyText: (
                    <Empty description='Продвигаемых магазинов нет — выдача обычная' />
                  )
                }}
                renderItem={(sellerId) => {
                  const seller = sellersById.get(sellerId)
                  return (
                    <List.Item style={{ padding: '8px 16px' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8
                        }}
                      >
                        <Button
                          size='small'
                          icon={<LeftOutlined />}
                          onClick={() => removeSeller(sellerId)}
                        />
                        {/* магазина нет в списке — значит, у него не осталось
                            опубликованных товаров либо он удалён */}
                        {seller ? (
                          renderSellerName(seller)
                        ) : (
                          <Tag color='warning'>магазин недоступен</Tag>
                        )}
                      </span>
                    </List.Item>
                  )
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  )
}
